import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card';
import { Button } from './ui/Button';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen w-full flex items-center justify-center bg-background p-4">
          <Card className="max-w-md w-full border-red-500/30 bg-surface/50 backdrop-blur-xl">
            <CardHeader>
              <CardTitle className="text-xl text-red-400 font-heading flex items-center gap-2">
                <span>Etwas ist schiefgelaufen</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-text-secondary text-sm italic">
                Die Anwendung ist auf einen unerwarteten Fehler gestoßen.
              </p>
              {this.state.error && (
                <div className="p-3 bg-black/20 rounded-lg border border-red-500/10 overflow-auto max-h-32">
                  <code className="text-xs text-red-300 break-all">
                    {this.state.error.message}
                  </code>
                </div>
              )}
              <Button 
                onClick={this.handleReset}
                variant="secondary"
                className="w-full mt-4"
              >
                Anwendung neu laden
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
